import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type StateLayerRadiusConstantType,
    type StateLayerStatusConstantType,
    type StateLayerColorConstantType,
    type StateLayerDynamicStyleType,
    type StateLayerOverlay,
    type StateLayerStatus
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";
import {
    getMotionTransition
} from "../../helpers/motion";
import {
    webStyle
} from "../../utils";

export const STATE_LAYER_OVERLAYS: Record<StateLayerStatus, StateLayerOverlay> = {
    rest: "transparent",
    pressed: "pressed",
    hovered: "hover"
};

export const getStateLayerStatus = ({
    isHovered,
    isPressed
}: StateLayerStatusConstantType): StateLayerStatus => {
    if(isPressed) {
        return "pressed";
    }

    if(isHovered) {
        return "hovered";
    }

    return "rest";
};

export const getStateLayerColor = ({
    overlayColor,
    isHovered,
    isPressed,
    colors
}: StateLayerColorConstantType): string => {
    const currentOverlay = STATE_LAYER_OVERLAYS[getStateLayerStatus({
        isHovered,
        isPressed
    })];

    if(currentOverlay === "transparent") {
        return "transparent";
    }

    return colors.system.state.overlay[currentOverlay][overlayColor];
};

export const getStateLayerRadius = ({
    borderRadius,
    borderWidth
}: StateLayerRadiusConstantType): number => {
    if(borderWidth >= borderRadius) {
        return 0;
    }

    return concentric({
        outer: borderRadius,
        inset: borderWidth,
        role: borderRadius
    });
};

const stylesheet = StyleSheet.create({
    container: {
        ...StyleSheet.absoluteFill,
        backgroundColor: "transparent",
        pointerEvents: "none"
    }
});

export const useStyles = ({
    isReduceMotionEnabled,
    borderRadius,
    overlayColor,
    shapeConfigs,
    borderWidth,
    isSquircle,
    durations,
    isHovered,
    isPressed,
    colors
}: StateLayerDynamicStyleType) => {
    const currentRadius = getStateLayerRadius({
        borderRadius,
        borderWidth
    });

    const styles = {
        container: {
            backgroundColor: getStateLayerColor({
                overlayColor,
                isHovered,
                isPressed,
                colors
            }),
            ...(isSquircle ? getCornerStyle({
                radius: currentRadius,
                shapeConfigs
            }) : {
                borderRadius: currentRadius
            })
        } as Mutable<ViewStyle>
    };

    const transition = getMotionTransition({
        isReduceMotionEnabled,
        properties: [
            "background-color"
        ],
        intent: "hover",
        durations
    });

    if(isPressed) {
        transition.transitionDuration = "0ms";
    }

    Object.assign(styles.container, webStyle(transition));

    return styles;
};
export default stylesheet;
